import { NavLink, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  ListSubheader,
} from "@mui/material";
import { LocalHospital as LocalHospitalIcon } from "@mui/icons-material";
import {
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  LockReset as LockResetIcon,
  Business as BusinessIcon,
  MedicalServices as MedicalServicesIcon,
  Factory as FactoryIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  BackupTable as BackupTableIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import { useSession } from "@/contexts/SessionContext";

const menuItems = [
  {
    title: "Patientinnen",
    adminOnly: false,
    items: [
      { title: "Patientinnen", url: "/patients", icon: AssignmentIcon },
      { title: "Datenexport", url: "/data-export", icon: CloudDownloadIcon },
    ],
  },
  {
    title: "Kunden",
    adminOnly: true,
    items: [
      { title: "Kundenverwaltung", url: "/customers", icon: BusinessIcon },
      { title: "Benutzerverwaltung", url: "/users", icon: PeopleIcon },
    ],
  },
  {
    title: "OP-Material",
    adminOnly: true,
    items: [
      { title: "Hersteller", url: "/manufacturers", icon: FactoryIcon },
      { title: "OP-Material", url: "/surgical-materials", icon: MedicalServicesIcon },
    ],
  },
  {
    title: "Administration",
    adminOnly: true,
    items: [
      { title: "Berichtswesen", url: "/reporting", icon: AssessmentIcon },
      { title: "Datenimport", url: "/data-import", icon: CloudUploadIcon },
      { title: "Datensicherung", url: "/datensicherung", icon: BackupTableIcon },
    ],
  },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const { remainingSeconds, isTracking } = useSession();
  const remainingMinutes = Math.max(0, Math.ceil(remainingSeconds / 60));
  const isWarning = isTracking && remainingSeconds <= 5 * 60; // letzten 5 Min hervorheben

  // Check if user is admin
  const isAdmin = useMemo(() => {
    try {
      return localStorage.getItem("isAdmin") === "true";
    } catch {
      return false;
    }
  }, []);

  const krankenhausName = useMemo(() => {
    try {
      return localStorage.getItem("krankenhausName") ?? "";
    } catch {
      return "";
    }
  }, []);

  // Filter menu items based on admin status
  const visibleMenuItems = useMemo(() => {
    return menuItems.filter(group => !group.adminOnly || isAdmin);
  }, [isAdmin]);

  const handleLogout = () => {
    // Clear all stored user data
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("isAdmin");
      localStorage.removeItem("userIk");
      localStorage.removeItem("krankenhausName");
      localStorage.removeItem("pendingUsername");
    } catch {}
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "primary.main" }}>
      <Toolbar>
        <Typography variant="h6" color="white" fontWeight="semibold">
          WHIK-Portal
        </Typography>
      </Toolbar>

      {krankenhausName && (
        <>
          <Box
            sx={{
              mx: 2,
              mb: 1,
              p: 1.5,
              bgcolor: "rgba(255, 255, 255, 0.12)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LocalHospitalIcon sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 20 }} />
            <Typography
              variant="body2"
              color="white"
              fontWeight="medium"
              sx={{ lineHeight: 1.3 }}
            >
              {krankenhausName}
            </Typography>
          </Box>
          <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", mb: 1 }} />
        </>
      )}

      <List>
        {visibleMenuItems.map((group) => (
          <Box key={group.title}>
            <ListSubheader
              sx={{
                bgcolor: "transparent",
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: "bold",
                lineHeight: "48px",
              }}
            >
              {group.title}
            </ListSubheader>
            <List component="div" disablePadding>
              {group.items.map((item) => (
                <ListItem key={item.title} disablePadding>
                  <ListItemButton
                    component={NavLink}
                    to={item.url}
                    sx={{
                      pl: 4,
                      color: "white",
                      "&.active": {
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        fontWeight: "bold",
                      },
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.15)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "white" }}>
                      <item.icon />
                    </ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }} />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/change-password"
            sx={{
              color: "white",
              "&.active": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                fontWeight: "bold",
              },
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.15)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "white" }}>
              <LockResetIcon />
            </ListItemIcon>
            <ListItemText primary="Passwort ändern" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.15)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "white" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Abmelden" />
          </ListItemButton>
        </ListItem>
      </List>

      {isTracking && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: isWarning ? "rgba(244, 67, 54, 0.25)" : "rgba(0, 0, 0, 0.15)",
            color: "white",
            borderTop: "1px solid rgba(255, 255, 255, 0.15)",
          }}
        >
          <TimerIcon fontSize="small" sx={{ opacity: 0.85 }} />
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Automatischer Logout in
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              fontFamily="monospace"
              aria-live="polite"
            >
              {remainingMinutes === 1 ? "1 Minute" : `${remainingMinutes} Minuten`}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
